export type RouteFailureReason = 'missing-pipe' | 'no-pipe-arrays';

/**
 * Route generation walks from the backfill plant to a stope's discharge point, and every leg needs a
 * pipe type to resolve. Deleting a pipe type does not cascade to the stopes that use it (see
 * ReticulationData.WarnStopesWithMissingPipes on the backend), so a dangling reference survives on
 * the document and surfaces here, at select time.
 *
 * Thrown rather than returned so the failure cannot be mistaken for "this stope legitimately has no
 * route": callers report the reason to the user so they know which pipe to reassign.
 */
export class RouteGenerationError extends Error {
  readonly reason: RouteFailureReason;

  readonly stopeId: string;

  readonly pipeId?: string;

  constructor(reason: RouteFailureReason, stopeId: string, pipeId?: string) {
    super(reason === 'missing-pipe'
      ? `Stope '${stopeId}' references pipe type '${pipeId}', which is not in the model.`
      : `Stope '${stopeId}' has no pipe arrays.`);
    this.name = 'RouteGenerationError';
    this.reason = reason;
    this.stopeId = stopeId;
    this.pipeId = pipeId;
    // Target is es5, so the prototype chain has to be restored for instanceof to hold.
    Object.setPrototypeOf(this, RouteGenerationError.prototype);
  }
}

export const missingPipeError = (stopeId: string, pipeId: string): RouteGenerationError => new RouteGenerationError('missing-pipe', stopeId, pipeId);

export const noPipeArraysError = (stopeId: string): RouteGenerationError => new RouteGenerationError('no-pipe-arrays', stopeId);

/**
 * Every caller of generateAllRoutes previously swallowed its throw and set an empty route map,
 * leaving the user with a blank model and no indication anything had gone wrong. Shared here so all
 * of them report the same actionable message.
 */
export const describeRouteFailure = (error: unknown): string => {
  if (!(error instanceof RouteGenerationError)) {
    return 'Could not trace a route to this stope.';
  }
  if (error.reason === 'missing-pipe') {
    return `Pipe type '${error.pipeId}' no longer exists in this model, so no route could be traced to this stope. `
      + 'Reassign the stope to a current pipe type to restore its routes.';
  }
  return 'This stope has no pipe arrays, so no route could be traced to it. Add a discharge pipe to the stope.';
};

/**
 * Stable notistack key so a failure on a repeating path (model refetch) shows once rather than
 * stacking a toast per refresh.
 */
export const routeFailureKey = (error: unknown): string => {
  if (!(error instanceof RouteGenerationError)) return 'route-failure';
  return `route-failure-${error.reason}-${error.stopeId}-${error.pipeId ?? ''}`;
};
